import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { setApiToken, clearApiToken } from '@/lib/api';
import { useWebSocket } from '@/hooks/useWebSocket';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Storage keys
const TOKEN_KEY = 'texhub_token';
const REMEMBER_KEY = 'texhub_remember';

// Safe storage access
const safeGetItem = (key) => {
  try {
    return localStorage.getItem(key) || sessionStorage.getItem(key);
  } catch (e) {
    return null;
  }
};

const safeSetItem = (key, value, useLocal = true) => {
  try {
    if (useLocal) {
      localStorage.setItem(key, value);
    } else {
      sessionStorage.setItem(key, value);
    }
    return true;
  } catch (e) {
    try {
      sessionStorage.setItem(key, value);
      return true;
    } catch (e2) {
      return false;
    }
  }
};

const safeRemoveItem = (key) => {
  try { localStorage.removeItem(key); } catch (e) {}
  try { sessionStorage.removeItem(key); } catch (e) {}
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [token, setToken] = useState(() => {
    const storedToken = safeGetItem(TOKEN_KEY);
    if (storedToken) {
      // Also set in API module's in-memory storage
      setApiToken(storedToken);
    }
    return storedToken;
  });
  
  // WebSocket connection for real-time notifications
  const { isConnected: wsConnected } = useWebSocket(token, setUnreadNotifications);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    // Check storage for token
    const storedToken = safeGetItem(TOKEN_KEY);
    if (!storedToken) {
      setLoading(false);
      return;
    }

    // Set token in API module
    setApiToken(storedToken);

    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
        credentials: 'include', // Include httpOnly cookies
        headers: {
          'Authorization': `Bearer ${storedToken}`
        }
      });

      if (response.ok) {
        const userData = await response.json();
        // Extract only serializable user fields
        const safeUser = {
          user_id: userData?.user_id || null,
          email: userData?.email || null,
          name: userData?.name || null,
          picture: userData?.picture || null,
          company_id: userData?.company_id || null,
          role: userData?.role || 'user',
          account_type: userData?.account_type || 'seller',
          subscription_plan: userData?.subscription_plan || 'free',
          onboarding_completed: userData?.onboarding_completed ?? true, // Default true for existing users
          products_count: userData?.products_count || 0
        };
        setUser(safeUser);
        setToken(storedToken);
      } else {
        // Clear both storages on invalid token
        localStorage.removeItem(TOKEN_KEY);
        sessionStorage.removeItem(TOKEN_KEY);
        setToken(null);
        setUser(null);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem(TOKEN_KEY);
      sessionStorage.removeItem(TOKEN_KEY);
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password, rememberMe = true) => {
    let response;
    try {
      response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        credentials: 'include', // Receive and store httpOnly cookies
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
    } catch (networkError) {
      console.error('Network error during login:', networkError);
      throw new Error('Error de conexión. Verifica tu internet.');
    }

    if (!response.ok) {
      let errorMessage = 'Credenciales inválidas';
      try {
        const errorData = await response.json();
        if (errorData.detail && typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        }
      } catch (e) {
        // Response was not JSON
      }
      throw new Error(errorMessage);
    }

    let data;
    try {
      data = await response.json();
    } catch (parseError) {
      console.error('JSON parse error:', parseError);
      throw new Error('Error al procesar la respuesta del servidor');
    }
    
    // Extract only serializable user fields
    const safeUser = {
      user_id: data.user?.user_id || null,
      email: data.user?.email || null,
      name: data.user?.name || null,
      picture: data.user?.picture || null,
      company_id: data.user?.company_id || null,
      role: data.user?.role || 'user',
      account_type: data.user?.account_type || 'seller',  // buyer or seller
      subscription_plan: data.user?.subscription_plan || 'free',
      country: data.user?.country || 'MX'
    };
    
    // CRITICAL: Set token in API module's in-memory storage first
    setApiToken(data.token);
    
    // Also try to persist in storage (may fail in restricted browsers)
    safeSetItem(TOKEN_KEY, data.token, rememberMe);
    if (rememberMe) {
      safeSetItem(REMEMBER_KEY, 'true', true);
    }
    
    setToken(data.token);
    setUser(safeUser);
    return safeUser;
  };

  const register = async (name, email, password, referredBy = null, companyData = null) => {
    let response;
    try {
      const payload = { name, email, password };
      if (referredBy) {
        payload.referred_by = referredBy;
      }
      // Add company data if provided (unified registration)
      if (companyData) {
        Object.assign(payload, companyData);
      }
      
      response = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        credentials: 'include', // Receive and store httpOnly cookies
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
    } catch (networkError) {
      console.error('Network error during register:', networkError);
      throw new Error('Error de conexión. Verifica tu internet.');
    }

    if (!response.ok) {
      let errorMessage = 'Error al registrarse';
      try {
        const errorData = await response.json();
        if (errorData.detail && typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        }
      } catch (e) {
        // Response was not JSON
      }
      throw new Error(errorMessage);
    }

    let data;
    try {
      data = await response.json();
    } catch (parseError) {
      console.error('JSON parse error:', parseError);
      throw new Error('Error al procesar la respuesta del servidor');
    }
    
    // Extract only serializable user fields
    const safeUser = {
      user_id: data.user?.user_id || null,
      email: data.user?.email || null,
      name: data.user?.name || null,
      picture: data.user?.picture || null,
      company_id: data.user?.company_id || null,
      role: data.user?.role || 'user',
      subscription_plan: data.user?.subscription_plan || 'free',
      referral_code: data.user?.referral_code || null,
      onboarding_completed: data.user?.onboarding_completed ?? false, // New users need onboarding
      products_count: data.user?.products_count || 0
    };
    
    // CRITICAL: Set token in API module's in-memory storage first
    setApiToken(data.token);
    
    // Also try to persist in storage
    safeSetItem(TOKEN_KEY, data.token, true);
    safeSetItem(REMEMBER_KEY, 'true', true);
    
    setToken(data.token);
    setUser(safeUser);
    return safeUser;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const processGoogleSession = async (sessionId) => {
    const response = await fetch(`${API_URL}/api/auth/session`, {
      method: 'POST',
      credentials: 'include', // Receive and store httpOnly cookies
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id: sessionId })
    });

    if (!response.ok) {
      throw new Error('Error al procesar la sesión');
    }

    const data = await response.json();
    
    // Extract only serializable user fields
    const safeUser = {
      user_id: data?.user_id || null,
      email: data?.email || null,
      name: data?.name || null,
      picture: data?.picture || null,
      company_id: data?.company_id || null,
      role: data?.role || 'user',
      subscription_plan: data?.subscription_plan || 'free'
    };
    
    // Save the JWT token for API calls
    if (data.token) {
      // CRITICAL: Set in API module first
      setApiToken(data.token);
      safeSetItem(TOKEN_KEY, data.token, true);
      safeSetItem(REMEMBER_KEY, 'true', true);
      setToken(data.token);
    }
    
    setUser(safeUser);
    return safeUser;
  };

  const logout = async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include' // Send cookie to be cleared server-side
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    // Clear all tokens including in-memory
    clearApiToken();
    safeRemoveItem(TOKEN_KEY);
    safeRemoveItem(REMEMBER_KEY);
    setToken(null);
    setUser(null);
  };

  const updateUser = (userData) => {
    setUser(prev => ({ ...prev, ...userData }));
  };
  
  // Mark notification as read (update count)
  const markNotificationRead = () => {
    setUnreadNotifications(prev => Math.max(0, prev - 1));
  };
  
  // Reset notification count
  const resetNotificationCount = (count) => {
    setUnreadNotifications(count);
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    loginWithGoogle,
    processGoogleSession,
    logout,
    updateUser,
    isAuthenticated: !!user,
    unreadNotifications,
    markNotificationRead,
    resetNotificationCount,
    wsConnected
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
